// src/components/support/forms/NonSatisfaction.js
import React, { useState } from "react";
import { ChevronLeft } from "lucide-react";
import moment from "moment";
import { DEFAULT_FORMAT } from "../../common/FootprintDatePicker";

import { useRequestProcessor } from "../../../requestProcessor";
import { useAtom } from "jotai";
import { appConstants, childList } from "../../../atomstore/store";
import { useNavigate } from "react-router-dom";

import SpinnerLoading from "../../common/SpinnerLoading";
import { handleNonSatisfactory } from "../../../queryfunctions/queryfunctions";

import FootprintDatePicker from "../../common/FootprintDatePicker";
import CustomSelect from "../../common/CustomSelect";

const NonSatisfaction = () => {
    const navigate = useNavigate();
    const { useMutationWrapper } = useRequestProcessor();
    const [selectedDate, setSelectedDate] = useState("");
    const [comment, setComment] = useState("");
    const [holidaySelected, setHolidaySelected] = useState(false);
    const [children] = useAtom(childList);
    const [constants] = useAtom(appConstants);
    const [selectedChild, setSelectedChild] = useState(0);
    const [error, setError] = useState("");
    const [successMsg, setSuccessMsg] = useState("");
    const [errorMsg, setErrorMsg] = useState("");

    const nonSatisfactory = useMutationWrapper(
        ["nonSatisfactory"],
        () => {
            return handleNonSatisfactory(
                {
                    selectedDate,
                    comment,
                    holidaySelected,
                    child: children[selectedChild]
                }
            );
        },
        {
            onSuccess: (data) => {
                if (data?.status !== 'ok') return;
                console.log("[Non Satisfaction Refund] Success:", data);
                setSuccessMsg("Your request has been submitted successfully.");
                setErrorMsg("");
            },
            onError: (err) => {
                setSuccessMsg("");
                setErrorMsg(err?.message || "Something went wrong. Please try again.");
            }
        }
    );

    const validate = () => {
        if (holidaySelected) {
            setError("The selected date cannot be a holiday");
            return false;
        }
        if (!selectedDate) {
            setError("Please select a date");
            return false;
        }
        if (!comment.trim()) {
            setError("Please enter a reason");
            return false;
        }
        setError("");
        return true;
    };

    const submit = () => {
        if (validate()) {
            nonSatisfactory.mutate();
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-50 z-50 overflow-y-auto">
            {nonSatisfactory.isLoading && <SpinnerLoading />}

            {/* Header */}
            <div className="sticky top-0 bg-white border-b p-4 flex items-center space-x-3">
                <button onClick={() => navigate(-1)}>
                    <ChevronLeft className="w-6 h-6 text-gray-600" />
                </button>
                <h2 className="text-lg font-bold text-gray-800 cursor-pointer" onClick={() => navigate(-1)}>
                    Non-Satisfaction Refund
                </h2>
            </div>

            {/* Body */}
            <div className="p-4 space-y-6">

                {children.length > 1 && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Child
                        </label>
                        <CustomSelect
                            value={selectedChild}
                            onChange={(val) => setSelectedChild(Number(val))}
                            options={children.map((child, idx) => ({
                                value: idx,
                                label: `${child.name} ${child.last_name}`,
                            }))}
                            placeholder="Select Child"
                        />
                    </div>
                )}
                {/* Info Banner */}
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-md">
                    <p className="text-sm text-blue-800">
                        {(constants?.nonSatisfactionRefundInfoBanner ||
                            "Submit a refund request for {{name}}.")
                            .replace(
                                "{{name}}",
                                `${children[selectedChild]?.name || ""} ${children[selectedChild]?.last_name || ""}`.trim()
                            )}
                    </p>
                </div>

                {/* Success / Error Messages */}
                {successMsg !== "" && (
                    <div className="bg-green-50 text-green-700 p-3 rounded-md font-medium">
                        {successMsg}
                    </div>
                )}
                {errorMsg !== "" && (
                    <div className="bg-red-50 text-red-700 p-3 rounded-md font-medium">
                        {errorMsg}
                    </div>
                )}

                {/* Date Picker */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Date
                    </label>

                    <FootprintDatePicker
                        holidaySelected={(isHoliday) => {
                            setHolidaySelected(isHoliday);
                            if (isHoliday) {
                                setError("The selected date cannot be a holiday");
                            }
                        }}
                        maximumDate={moment()}
                        minimumDate={moment().subtract(2, "days")}
                        onChange={(date) => {
                            setSelectedDate(moment(date).format(DEFAULT_FORMAT));
                            setHolidaySelected(false);
                            setError("");
                        }}
                        inputProps={{ placeholder: "Select Date" }}
                    />
                </div>

                {/* Reason Textarea (Native, No FootprintInputText) */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Reason
                    </label>
                    <textarea
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl 
                                   focus:ring-2 focus:ring-cyan-500 h-32 outline-none focus:border-transparent"
                        placeholder="Enter reason..."
                        value={comment}
                        onChange={(e) => {
                            setComment(e.target.value);
                            setError("");
                        }}
                    />
                </div>

                {/* Error */}
                {error && <p className="text-red-500 text-sm">{error}</p>}

                {/* Submit Button */}
                <button
                    onClick={submit}
                    disabled={nonSatisfactory.isLoading}
                    className="w-full bg-cyan-500 text-white py-3 rounded-xl font-semibold 
                               hover:bg-cyan-600 transition disabled:opacity-60"
                >
                    Submit Request
                </button>
            </div>
        </div>
    );
};

export default NonSatisfaction;
